import React, { useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Flex,
  Badge,
  IconButton,
  Spinner,
  Center,
  useColorModeValue,
  useToast,
  Select,
  SimpleGrid,
  Avatar,
  Spacer
} from '@chakra-ui/react';
import { FiThumbsUp, FiThumbsDown, FiShare2, FiBookOpen } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import IconWrapper from '../components/IconWrapper';
import CachedImage from '../components/CachedImage';
import { useAuth } from '../contexts/AuthContext';
import { useNovels } from '../contexts/UnifiedContentContext';
import { GENRES } from '../constants/genres';

const Novels: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const {
    novels,
    loading,
    error,
    selectedGenre,
    userLikes,
    likeCounts,
    likingNovels,
    loadNovels,
    setSelectedGenre,
    handleLike,
    handleShare
  } = useNovels();

  // Color mode values
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.800', 'white');
  const titleColor = useColorModeValue('gray.900', 'white');
  const labelColor = useColorModeValue('gray.600', 'gray.400');

  const genres = [
    { id: 'all', name: 'All Genres' },
    ...GENRES.map(genre => ({ id: genre.toLowerCase(), name: genre }))
  ];

  useEffect(() => {
    loadNovels(user?.uid);
  }, [selectedGenre, loadNovels, user?.uid]);

  const onGenreChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedGenre(e.target.value);
  };

  if (loading) {
    return (
      <Center h="100vh">
        <Spinner size="xl" color="purple.500" />
      </Center>
    );
  }

  if (error || novels.length === 0) {
    return (
      <Center h="50vh">
        <VStack gap={6} textAlign="center" maxW="400px">
          <Text fontSize="2xl" fontWeight="bold" color={titleColor}>
            Content Not Found
          </Text>
          <Text fontSize="md" color={labelColor}>
            Content not found or no longer available
          </Text>
          <VStack gap={3}>
            <Button onClick={() => loadNovels(user?.uid)} colorScheme="purple" size="lg">
              Refresh
            </Button>
            <Button 
              onClick={() => navigate('/upload')} 
              variant="outline" 
              colorScheme="purple"
            >
              Upload Content
            </Button>
          </VStack>
        </VStack>
      </Center>
    );
  }

  return (
    <Box bg={bgColor} minH="100vh" px={4} py={6}>
      <VStack gap={6} maxW="1400px" mx="auto">
        {/* Header */}
        <Flex justify="space-between" align="center" w="full">
          <Text fontSize="3xl" fontWeight="bold" color={titleColor}>
            Novels
          </Text>
          <Select
            value={selectedGenre}
            onChange={onGenreChange}
            placeholder="Filter by Genre"
            maxW="200px"
            bg={cardBg}
            color={textColor}
            borderColor={labelColor}
          >
            {genres.map((genre) => (
              <option key={genre.id} value={genre.id}>
                {genre.name}
              </option>
            ))}
          </Select>
        </Flex>

        {/* Novels Grid */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4 }} spacing={6} w="full">
          {novels.map((novel) => (
            <Box
              key={novel.id}
              bg={cardBg}
              borderRadius="lg"
              shadow="md"
              overflow="hidden"
              _hover={{ transform: 'translateY(-4px)', shadow: 'lg' }}
              transition="all 0.2s"
            >
              {/* Thumbnail */}
              <Box position="relative" h="200px">
                <CachedImage
                  src={novel.poster || novel.thumbnail}
                  alt={novel.title}
                  w="100%"
                  h="100%"
                  objectFit="cover"
                />
                <Badge
                  position="absolute"
                  top={2}
                  left={2}
                  colorScheme="green"
                  fontSize="xs"
                  px={2}
                  py={1}
                  borderRadius="md"
                >
                  {novel.category}
                </Badge>
                <IconButton
                  aria-label="View Novel"
                  icon={<IconWrapper icon={FiBookOpen} size={24} />}
                  position="absolute"
                  bottom={2}
                  right={2}
                  colorScheme="green"
                  size="lg"
                  borderRadius="full"
                  onClick={() => navigate(`/book/${novel.id}`)}
                />
              </Box>

              {/* Content Info */}
              <VStack p={4} align="flex-start" spacing={2}>
                <Text fontSize="lg" fontWeight="bold" color={textColor} noOfLines={1}>
                  {novel.title}
                </Text>
                <HStack spacing={2} w="full" justify="space-between">
                  <HStack>
                    <Avatar size="xs" src={novel.uploaderAvatar} name={novel.uploader} />
                    <Text fontSize="sm" color={labelColor}>
                      {novel.uploader}
                    </Text>
                  </HStack>
                  <Text fontSize="sm" color={labelColor}>
                    {novel.views.toLocaleString()} views
                  </Text>
                </HStack>

                {/* Actions */}
                <HStack spacing={2} pt={2} w="full">
                  <Button
                    leftIcon={<IconWrapper icon={FiThumbsUp} size={16} />}
                    size="sm"
                    colorScheme={userLikes[novel.id] === 'like' ? 'green' : 'gray'}
                    variant={userLikes[novel.id] === 'like' ? 'solid' : 'outline'}
                    onClick={() => handleLike(novel.id, user!.uid)}
                    isLoading={likingNovels.has(novel.id)}
                    isDisabled={!user}
                  >
                    {likeCounts[novel.id]?.likes || 0}
                  </Button>
                  <Button
                    leftIcon={<IconWrapper icon={FiThumbsDown} size={16} />}
                    size="sm"
                    colorScheme={userLikes[novel.id] === 'dislike' ? 'red' : 'gray'}
                    variant={userLikes[novel.id] === 'dislike' ? 'solid' : 'outline'}
                    onClick={() => handleLike(novel.id, user!.uid)}
                    isLoading={likingNovels.has(novel.id)}
                    isDisabled={!user}
                  >
                    {likeCounts[novel.id]?.dislikes || 0}
                  </Button>
                  <Spacer />
                  <IconButton
                    aria-label="Share Novel"
                    icon={<IconWrapper icon={FiShare2} size={16} />}
                    size="sm"
                    variant="outline"
                    colorScheme="purple"
                    onClick={() => handleShare(novel.id, novel.category, 'social', 'web', user?.uid)}
                    isDisabled={!user}
                  />
                </HStack>
              </VStack>
            </Box>
          ))}
        </SimpleGrid>
      </VStack>
    </Box>
  );
};

export default Novels;
